import { createContext, useState, useEffect, useContext, useCallback } from 'react'
import { AuthContext } from '../../../context/AuthContext'
import { SessionContext } from '../../../context/SessionContext'
import { checkSurveyCompleted, submitSurvey } from '../service/surveyService'

export const SurveyContext = createContext()

export const SurveyProvider = ({ children }) => {
  const { currentUser } = useContext(AuthContext)
  const { session } = useContext(SessionContext)

  // Track completion status for each survey type
  const [completionStatus, setCompletionStatus] = useState({})
  const [loading, setLoading] = useState(true)

  // Survey types to check on mount
  const SURVEY_TYPES = ['pre_task', 'pre_quiz', 'mid_session', 'post_task']

  useEffect(() => {
    const initialize = async () => {
      if (!currentUser?.id || !session?.id) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const status = {}

        for (const type of SURVEY_TYPES) {
          const result = await checkSurveyCompleted(currentUser.id, session.id, type)
          status[type] = result?.completed ?? false
        }

        setCompletionStatus(status)
      } catch (err) {
        console.error('Error initializing surveys:', err)
        // Default all to false
        const status = {}
        SURVEY_TYPES.forEach(type => { status[type] = false })
        setCompletionStatus(status)
      } finally {
        setLoading(false)
      }
    }

    initialize()
  }, [currentUser?.id, session?.id])

  const isCompleted = useCallback((surveyType) => {
    return completionStatus[surveyType] ?? false
  }, [completionStatus])

  const submit = useCallback(async (surveyType, responses) => {
    try {
      await submitSurvey(currentUser.id, session.id, surveyType, responses)
      setCompletionStatus(prev => ({ ...prev, [surveyType]: true }))
    } catch (err) {
      console.error(`Error submitting ${surveyType} survey:`, err)
      throw err
    }
  }, [currentUser?.id, session?.id])

  return (
    <SurveyContext.Provider value={{
      isCompleted,
      submit,
      loading,
    }}>
      {children}
    </SurveyContext.Provider>
  )
}